import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { FiArrowLeft, FiCreditCard, FiUser, FiBriefcase, FiMapPin, FiCalendar, FiDollarSign, FiTag, FiGlobe, FiCloud } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActivityPolicyByIdRequest } from '../../store/actions/policiesActions';
import { createEcmwfForecast } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import PolicySumInsuredRows from '../../components/Common/PolicySumInsuredFields';
import './AdminActivityPolicyDetail.css';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children || 'N/A'}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminActivityPolicyDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { policyId } = useParams();
    const { selectedPolicy } = useSelector((state) => state.policies);
    const { t } = useTranslation();

    const [showForecastModal, setShowForecastModal] = useState(false);
    const [forecastLoading, setForecastLoading] = useState(false);
    const [forecastError, setForecastError] = useState(null);
    const [forecastData, setForecastData] = useState(null);

    useEffect(() => {
        if (policyId) {
            dispatch(fetchActivityPolicyByIdRequest(policyId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policyId]);

    const policyInfo = selectedPolicy?.data?.policies_info || selectedPolicy?.data?.policy_info || selectedPolicy?.data || null;
    const clientInfo = policyInfo?.client_info || {};
    const activityInfo = policyInfo?.activity_info || {};
    const partnerInfo = policyInfo?.partner_info || {};
    const referenceInfo = policyInfo?.reference_info || {};
    const subCoverage = policyInfo?.subcoverage || null;
    const subCoverages = policyInfo?.reference_info?.SubCoverage || [];

    const handleOpenForecastModal = () => {
        setShowForecastModal(true);
        setForecastError(null);
        setForecastData(null);
    };

    const handleCloseForecastModal = () => {
        setShowForecastModal(false);
        setForecastError(null);
        setForecastData(null);
    };

    const handleCreateForecast = async () => {
        if (!policyId) {
            setForecastError(t('pages.activityPolicyDetail.forecast.error'));
            return;
        }

        try {
            setForecastLoading(true);
            setForecastError(null);
            setForecastData(null);
            const data = await createEcmwfForecast(policyId);
            setForecastData(data);
            
            // Rafraîchir les données de la police après la création
            dispatch(fetchActivityPolicyByIdRequest(policyId));
        } catch (e) {
            setForecastError(handleApiError(e, t('pages.activityPolicyDetail.forecast.error')));
        } finally {
            setForecastLoading(false);
        }
    };

    if (selectedPolicy.loading) {
        return (
            <div className="dashboard-page admin-activity-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.activityPolicyDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/policies')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">{t('pages.activityPolicyDetail.loading')}</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (selectedPolicy.error) {
        return (
            <div className="dashboard-page admin-activity-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.activityPolicyDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/policies')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <Alert variant="danger">{selectedPolicy.error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!policyInfo) {
        return (
            <div className="dashboard-page admin-activity-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.activityPolicyDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/policies')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <p className="text-muted">{t('pages.activityPolicyDetail.notFound')}: {policyId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-activity-policy-detail-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiCreditCard className="me-2" />
                                {t('pages.activityPolicyDetail.title')}
                            </h2>
                            <p className="text-muted mb-0">{t('pages.activityPolicyDetail.policyCode')}: {policyInfo.policy_code || 'N/A'}</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="primary" 
                                onClick={handleOpenForecastModal}
                                disabled={forecastLoading || !policyId}
                            >
                                <FiCloud className="me-2" />
                                {forecastLoading ? t('pages.activityPolicyDetail.forecast.creating') : t('pages.activityPolicyDetail.createForecast')}
                            </Button>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/policies')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                    </div>

                    {/* Informations principales de la police */}
                    <Section title={t('pages.activityPolicyDetail.sections.mainInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Code police</Label>
                                <Value>{policyInfo.policy_code}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Statut</Label>
                                <Value>
                                    <Badge bg={
                                        policyInfo.status === 'Created' ? 'success' :
                                            policyInfo.status === 'Paid' ? 'primary' :
                                                policyInfo.status === 'CG confirmed' ? 'info' :
                                                    policyInfo.status === 'Cancelled' ? 'danger' : 'secondary'
                                    }>
                                        {policyInfo.status}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Date UW</Label>
                                <Value>{policyInfo.uw_date || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Code devis</Label>
                                <Value>{policyInfo.quote_code || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Date soumission RIB</Label>
                                <Value>{policyInfo.date_rib_submission || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Mode de traitement</Label>
                                <Value>{policyInfo.handling_mode || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Langue préférée</Label>
                                <Value>{policyInfo.preferred_language || 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations financières */}
                    <Section title={t('pages.activityPolicyDetail.sections.financialInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Prix assurance HT</Label>
                                <Value>{policyInfo.insurance_price_excl_tsca || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Ratio prix Orakle HT</Label>
                                <Value>{policyInfo.orakle_price_ratio_excl_tsca || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Prix TTC</Label>
                                <Value>{policyInfo.price_incl_tsca || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Montant TSCA</Label>
                                <Value>{policyInfo['tsca_amount '] || policyInfo.tsca_amount || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Taux TSCA</Label>
                                <Value>{policyInfo.tsca || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Prime technique</Label>
                                <Value>{policyInfo.technical_premium || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Montant prime technique</Label>
                                <Value>{policyInfo.technical_premium_amount || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Montant TVA</Label>
                                <Value>{policyInfo.tva_amount || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Pourcentage TVA</Label>
                                <Value>{policyInfo.tva_percent || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <PolicySumInsuredRows source={policyInfo} Label={Label} Value={Value} />
                    </Section>

                    {/* Informations client */}
                    <Section title={t('pages.activityPolicyDetail.sections.clientInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Nom</Label>
                                <Value>{clientInfo.first_name} {clientInfo.last_name}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Email</Label>
                                <Value>{clientInfo.email}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Code client</Label>
                                <Value>{clientInfo.client_code || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Statut</Label>
                                <Value>
                                    <Badge bg={clientInfo.status === 'Active' ? 'success' : 'secondary'}>
                                        {clientInfo.status || 'N/A'}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Date d'inscription</Label>
                                <Value>{clientInfo.date_joined || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Date de naissance</Label>
                                <Value>{clientInfo.birth_date || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Ville</Label>
                                <Value>{clientInfo.city || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Pays</Label>
                                <Value>{clientInfo.country || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Code postal</Label>
                                <Value>{clientInfo.zip_code || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Téléphone</Label>
                                <Value>{clientInfo.phone_number || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Lieu de naissance</Label>
                                <Value>{clientInfo.place_of_birth || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Pays de naissance</Label>
                                <Value>{clientInfo.country_of_birth || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>IBAN reçu</Label>
                                <Value>
                                    <Badge bg={clientInfo.is_iban_received ? 'success' : 'secondary'}>
                                        {clientInfo.is_iban_received ? 'Oui' : 'Non'}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations activité */}
                    <Section title={t('pages.activityPolicyDetail.sections.activityInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Nom</Label>
                                <Value>{activityInfo.name || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Catégorie</Label>
                                <Value>{activityInfo.category || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Code activité</Label>
                                <Value>{activityInfo.activity_code || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Date de début</Label>
                                <Value>{activityInfo.activity_start_date || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Date de fin</Label>
                                <Value>{activityInfo.activity_end_date || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Heure de début</Label>
                                <Value>{activityInfo.activity_start_time || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Heure de fin</Label>
                                <Value>{activityInfo.activity_end_time || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Prix activité</Label>
                                <Value>{activityInfo.activity_price || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Durée</Label>
                                <Value>{activityInfo.duration || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Numéro de réservation</Label>
                                <Value>{activityInfo.reservation_number || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={12}>
                                <Label>Adresse</Label>
                                <Value>{activityInfo.activity_address || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={4}>
                                <Label>Ville</Label>
                                <Value>{activityInfo.activity_city || 'N/A'}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Code postal</Label>
                                <Value>{activityInfo.activity_zip_code || 'N/A'}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Pays</Label>
                                <Value>{activityInfo.activity_country || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Latitude</Label>
                                <Value>{activityInfo.latitude || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Longitude</Label>
                                <Value>{activityInfo.longitude || 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations partenaire */}
                    <Section title={t('pages.activityPolicyDetail.sections.partnerInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Nom</Label>
                                <Value>{partnerInfo.partner_first_name} {partnerInfo.partner_last_name}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Entreprise</Label>
                                <Value>{partnerInfo.company_name || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Email</Label>
                                <Value>{partnerInfo.email || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Téléphone</Label>
                                <Value>{partnerInfo.phone_number || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Ville</Label>
                                <Value>{partnerInfo.city || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Pays</Label>
                                <Value>{partnerInfo.country || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Code</Label>
                                <Value>{partnerInfo.code || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Commission</Label>
                                <Value>{partnerInfo.commission || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Compte activé</Label>
                                <Value>
                                    <Badge bg={partnerInfo.activate_account ? 'success' : 'secondary'}>
                                        {partnerInfo.activate_account ? 'Oui' : 'Non'}
                                    </Badge>
                                </Value>
                            </Col>
                            <Col md={6}>
                                <Label>Vérifié</Label>
                                <Value>
                                    <Badge bg={partnerInfo.is_verified ? 'success' : 'secondary'}>
                                        {partnerInfo.is_verified ? 'Oui' : 'Non'}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations référence */}
                    <Section title={t('pages.activityPolicyDetail.sections.referenceInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Nom</Label>
                                <Value>{referenceInfo.reference_name || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Code référence</Label>
                                <Value>{referenceInfo.reference_code || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Catégorie</Label>
                                <Value>{referenceInfo.reference_category || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Déclenchement cumulé</Label>
                                <Value>
                                    <Badge bg={referenceInfo.prcp_is_cumulated ? 'warning' : 'secondary'}>
                                        {referenceInfo.prcp_is_cumulated ? 'Oui' : 'Non'}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations sous-couverture */}
                    {(subCoverage || (subCoverages && subCoverages.length > 0)) && (
                        <Section title={t('pages.activityPolicyDetail.sections.subCoverage')}>
                            {/* Afficher subcoverage si c'est un objet unique */}
                            {subCoverage && (
                                <div className="mb-4">
                                    <Row>
                                        <Col md={6}>
                                            <Label>Code sous-couverture</Label>
                                            <Value>{subCoverage.coverage_code || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Type de déclencheur</Label>
                                            <Value>{subCoverage.trigger_type || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Unité</Label>
                                            <Value>{subCoverage.unit || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Nombre d'heures de déclenchement</Label>
                                            <Value>{subCoverage.trigger_number_hours || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Heure de déclenchement inférieure</Label>
                                            <Value>{subCoverage.prcp_trigger_hour_inf || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Déclenchement bas</Label>
                                            <Value>{subCoverage.trigger_low || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Date début sous-couverture</Label>
                                            <Value>{subCoverage.sub_coverage_start_date || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Date fin sous-couverture</Label>
                                            <Value>{subCoverage.sub_coverage_end_date || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Heure de timing</Label>
                                            <Value>{subCoverage.timing_hour || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Jour de timing</Label>
                                            <Value>{subCoverage.timing_day || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Heure d'observation</Label>
                                            <Value>{subCoverage.observation_timing_hour || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Jour d'observation</Label>
                                            <Value>{subCoverage.observation_timing_day || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Ratio SIV maintenu</Label>
                                            <Value>{subCoverage.siv_ratio_maintained || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Ratio SIV annulé</Label>
                                            <Value>{subCoverage.siv_ratio_cancelled || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Ratio prix Orakle HT</Label>
                                            <Value>{subCoverage.orakle_price_ratio_excl_tsca || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Source de données</Label>
                                            <Value>{subCoverage.data_source || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Ville</Label>
                                            <Value>{subCoverage.city || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Code postal</Label>
                                            <Value>{subCoverage.zip_code || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Pays</Label>
                                            <Value>{subCoverage.country || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Adresse activité</Label>
                                            <Value>{subCoverage.activity_address || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Latitude</Label>
                                            <Value>{subCoverage.geo_latitude || 'N/A'}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Longitude</Label>
                                            <Value>{subCoverage.geo_longitude || 'N/A'}</Value>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                            {/* Afficher subCoverages si c'est un array */}
                            {subCoverages && subCoverages.length > 0 && subCoverages.map((subCoverageArray, arrayIndex) => (
                                subCoverageArray && Array.isArray(subCoverageArray) && subCoverageArray.map((subCoverageItem, index) => (
                                    <div key={`${arrayIndex}-${index}`} className="mb-4">
                                        <h6 className="mb-3">Sous-couverture {index + 1}</h6>
                                        <Row>
                                            <Col md={6}>
                                                <Label>Code sous-couverture</Label>
                                                <Value>{subCoverageItem.coverage_code || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Type de déclencheur</Label>
                                                <Value>{subCoverageItem.trigger_type || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Unité</Label>
                                                <Value>{subCoverageItem.unit || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Nombre d'heures de déclenchement</Label>
                                                <Value>{subCoverageItem.trigger_number_hours || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Heure de déclenchement inférieure</Label>
                                                <Value>{subCoverageItem.prcp_trigger_hour_inf || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Déclenchement bas</Label>
                                                <Value>{subCoverageItem.trigger_low || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Date début sous-couverture</Label>
                                                <Value>{subCoverageItem.sub_coverage_start_date || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Date fin sous-couverture</Label>
                                                <Value>{subCoverageItem.sub_coverage_end_date || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Heure de timing</Label>
                                                <Value>{subCoverageItem.timing_hour || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Jour de timing</Label>
                                                <Value>{subCoverageItem.timing_day || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Heure d'observation</Label>
                                                <Value>{subCoverageItem.observation_timing_hour || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Jour d'observation</Label>
                                                <Value>{subCoverageItem.observation_timing_day || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Ratio SIV maintenu</Label>
                                                <Value>{subCoverageItem.siv_ratio_maintained || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Ratio SIV annulé</Label>
                                                <Value>{subCoverageItem.siv_ratio_cancelled || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Ratio prix Orakle HT</Label>
                                                <Value>{subCoverageItem.orakle_price_ratio_excl_tsca || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Source de données</Label>
                                                <Value>{subCoverageItem.data_source || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Ville</Label>
                                                <Value>{subCoverageItem.city || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Code postal</Label>
                                                <Value>{subCoverageItem.zip_code || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Pays</Label>
                                                <Value>{subCoverageItem.country || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Adresse activité</Label>
                                                <Value>{subCoverageItem.activity_address || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Label>Latitude</Label>
                                                <Value>{subCoverageItem.geo_latitude || 'N/A'}</Value>
                                            </Col>
                                            <Col md={6}>
                                                <Label>Longitude</Label>
                                                <Value>{subCoverageItem.geo_longitude || 'N/A'}</Value>
                                            </Col>
                                        </Row>
                                        {index < subCoverageArray.length - 1 && <hr className="my-4" />}
                                    </div>
                                ))
                            ))}
                        </Section>
                    )}
                </Card.Body>
            </Card>

            {/* Modal pour créer une prévision ECMWF */}
            <Modal show={showForecastModal} onHide={handleCloseForecastModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiCloud className="me-2" />
                        {t('pages.activityPolicyDetail.forecast.title')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {forecastLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">{t('pages.activityPolicyDetail.forecast.creating')}</p>
                        </div>
                    ) : forecastError ? (
                        <Alert variant="danger">
                            <strong>{t('common.error')} :</strong> {forecastError}
                        </Alert>
                    ) : forecastData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>{t('pages.activityPolicyDetail.forecast.success')}</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>{t('pages.activityPolicyDetail.forecast.info')}</strong>
                                </Card.Header>
                                <Card.Body>
                                    {forecastData.forecast_id && (
                                        <p className="mb-2">
                                            <strong>{t('pages.activityPolicyDetail.forecast.forecastId')} :</strong> {forecastData.forecast_id}
                                        </p>
                                    )}
                                    {forecastData.policy_id && (
                                        <p className="mb-2">
                                            <strong>{t('pages.activityPolicyDetail.forecast.policyId')} :</strong> {forecastData.policy_id}
                                        </p>
                                    )}
                                    {forecastData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {forecastData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{ 
                                            whiteSpace: 'pre-wrap', 
                                            wordBreak: 'break-word', 
                                            fontSize: '0.85rem', 
                                            maxHeight: '300px', 
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(forecastData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : (
                        <div>
                            <Alert variant="info" className="mb-3">
                                <strong>Information :</strong> Cette action va créer une prévision ECMWF pour cette police d'assurance.
                            </Alert>
                            {policyInfo && (
                                <Card>
                                    <Card.Header>
                                        <strong>Informations de la police</strong>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="mb-2">
                                            <strong>ID de la police :</strong> {policyId}
                                        </p>
                                        <p className="mb-2">
                                            <strong>Code police :</strong> {policyInfo.policy_code || 'N/A'}
                                        </p>
                                        {policyInfo.status && (
                                            <p className="mb-2">
                                                <strong>Statut :</strong> {policyInfo.status}
                                            </p>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {forecastData ? (
                        <Button variant="secondary" onClick={handleCloseForecastModal}>
                            Fermer
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={handleCloseForecastModal}>
                                Annuler
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleCreateForecast}
                                disabled={forecastLoading || !policyId}
                            >
                                {forecastLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Création...
                                    </>
                                ) : (
                                    <>
                                        <FiCloud className="me-2" />
                                        {t('pages.activityPolicyDetail.forecast.create')}
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminActivityPolicyDetail;
